<template>
  <!-- Backdrop -->
  <div v-if="openFilterModal" class="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ease-in-out" @click="emit('close')"></div>

  <!-- Drawer -->
  <div :class="['filter-drawer  flex flex-col p-8 overflow-y-auto pr-5', { open: openFilterModal }]">
    
    <!-- Drawer Header -->
    <div class="flex items-center justify-between py-4">
      <span class="font-bold responsive-title-6">{{ t('filters') }}</span>
      <button class="cursor-pointer btnClose" @click="emit('close')">
        <img src="@/assets/icons/svg/Close_16x16.svg" alt="close" width="16"/>
      </button>
    </div>

    <!-- Search Recommendation Button -->
    <div class="search-results__mtitle font-bold mb-2.5">
      <CustomButton @click="$router.push({ path: '/recommendations', query: searchStore.recommendationQuery })"
                    type="black"
                    :outlined="false"
                    :rounded="true"
                    class="arrow__btn border transition-all w-full md:py-1 md:px-5 py-1 px-3 flex items-center justify-center rounded-12xl cursor-pointer gap-2">
        {{ t('global.ui.exploreRecommendations') }}</CustomButton>
    </div>

    <hr/>

    <!-- Scrollable Filters -->
    <div class="flex-grow overflow-y-auto space-y-6 pt-5 mb-24">
      
      <SearchRemoteSelectionFilter :title="t('destination')"
                                   placeholder="Select Destination"
                                   v-model="searchStore.selectedFilters.destinations"
                                   :options="searchStore.filters.destinations"
                                   :loading="searchStore.destinationsLoading"
                                   :remote="searchStore.searchDestinations"
                                   
      />
      
      <hr/>

      <div class="font-bold responsive-title-6"> {{ t('when') }}</div>
      
      <DateInput
          v-model:dateFrom="searchStore.selectedFilters.dateFrom"
          v-model:dateTo="searchStore.selectedFilters.dateTo"
          :has-icon="false"
          variant="plain"
          @on-date-select="searchStore.setDates"
          :show-tabs="false"
          :columns="1"
          :rows="1"
          :calendarClass="'scale-[1.0]'"
      >
        <template #display="slotProps">
                  <span
                      class="border border-gray rounded p-2 flex-1 hover:cursor-pointer"
                      @click="slotProps.toggleOpen"
                  >
                    {{ slotProps.startString }} to {{ slotProps.endString }}
                  </span>
        </template>
      </DateInput>
      <hr/>
      <div class="flex flex-col gap-y-6 "
           v-if="!searchStore.isInitialSetup && Object.keys(searchStore.filterMetas).length">
        <SearchCheckboxFilter :options="searchStore.filters.categories"
                              :title="t('categories')"
                              v-model="searchStore.selectedFilters.categories"
                              v-if="searchStore.filters.categories"
        />

        <hr v-if="searchStore.filters.categories"/>
        <SearchCheckboxFilter :options="searchStore.filters.charterTypes"
                              v-model="searchStore.selectedFilters.charterTypes"
                              :title="t('types')"
                              v-if="searchStore.filters.charterTypes"
        />
        <hr v-if="searchStore.filters.charterTypes"/>

        <SearchRangeFilter
            :title="t('budget')"
            :range="[searchStore.filterMetas.price.value.min,searchStore.filterMetas.price.value.max]"
            v-model="searchStore.selectedFilters.price"
            :bin="searchStore.filterMetas.price.value.bins"
            type="currency"
            @update:value="args => { searchStore.selectedFilters.price = args}"
        />

        <hr/>
        <SearchRangeFilter
            :title="t('yearOfManufacture')"
            :range="[searchStore.filterMetas.year.value.min,searchStore.filterMetas.year.value.max]"
            v-model="searchStore.selectedFilters.year"
            :bin="searchStore.filterMetas.year.value.bins"
            @update:value="args => { searchStore.selectedFilters.year = args}"
        />

        <hr/>
        <SearchRangeFilter
            :title="t('cabins')"
            :range="[searchStore.filterMetas.cabin.value.min,searchStore.filterMetas.cabin.value.max]"
            v-model="searchStore.selectedFilters.cabins"
            :bin="searchStore.filterMetas.cabin.value.bins"
            @update:value="args => { searchStore.selectedFilters.cabins = args}"
        />

        <hr/>

        <SearchRangeFilter
            :title="t('toilets')"
            :range="[searchStore.filterMetas.wc.value.min,searchStore.filterMetas.wc.value.max]"
            v-model="searchStore.selectedFilters.wc"
            :bin="searchStore.filterMetas.wc.value.bins"
            @update:value="args => { searchStore.selectedFilters.wc = args}"
        />

        <hr/>

        <SearchRangeFilter
            :title="t('berths')"
            :range="[searchStore.filterMetas.berth.value.min,searchStore.filterMetas.berth.value.max]"
            v-model="searchStore.selectedFilters.berths"
            :bin="searchStore.filterMetas.berth.value.bins"
            @update:value="args => { searchStore.selectedFilters.berths = args}"
        />

        <hr/>

        <SearchRangeFilter
            :title="t('length')"
            :range="[searchStore.filterMetas.length.value.min,searchStore.filterMetas.length.value.max]"
            v-model="searchStore.selectedFilters.length"
            :bin="searchStore.filterMetas.length.value.bins"
            type="length"
            @update:value="args => { searchStore.selectedFilters.length = args}"
        />

        <hr/>

        <SearchSelectionFilter title="Shipyards"
                               placeholder="Select filter"
                               v-model="searchStore.selectedFilters.shipyards"
                               :options="searchStore.filters.shipyards.map((shipyard) => ({ value: shipyard.id, label: shipyard.name }))"

        />

        <hr/>

        <SearchSelectionFilter :title="t('models')"
                               placeholder="Select Models"
                               v-model="searchStore.selectedFilters.models"
                               :options="searchStore.filters.models.map((model) => ({ value: model.id, label: model.name }))"
        />
        <hr/>

        <SearchCheckboxFilter
            :options="searchStore.computedOrderedSailItems"
            v-model="searchStore.selectedFilters.sailTypeIds"
            :title="t('sailTypes')"
            v-if="searchStore.computedOrderedSailItems"
        />

      </div>
      <div v-else class="flex flex-col gap-8">
        <div class="flex justify-between">
          <el-skeleton animated :rows="0"/>
          <el-skeleton class="flex justify-end" animated :rows="0"/>
        </div>
        <hr/>
        <el-skeleton animated :rows="1"/>
        <hr/>
        <el-skeleton animated :rows="1"/>
        <hr/>
        <el-skeleton animated :rows="1"/>
      </div>
    </div>
      <!-- Footer -->
    <div class="bg-white flex justify-between flex-shrink-0 py-8 px-8 absolute bottom-0 left-0 w-full border-t bg-gradient-to-br from-gradients-ghostWhite to-gradients-aliceBlue">
        <button class="text-black font-semibold underline" @click="searchStore.onClearFilters()">
          {{ t('clearAll') }}
        </button>

      
<!--      Add a spinner here in the future  -->
      <CustomButton type="black"
                    :rounded="true"
                    :outlined="false"
                    @click="emit('close')"
      >
        <span> {{ t('show') }} ({{ searchStore.paginationData.totalElements || 0 }})</span>
      </CustomButton>
      </div>

  </div>

  
</template>

<script setup lang="ts">
import { useSearchStore } from "@/stores/searchStore";
import { useI18n } from 'vue-i18n';
import SearchRemoteSelectionFilter from "@/pages/SearchResults/filters/SearchRemoteSelectionFilter.vue";
import SearchCheckboxFilter from "@/pages/SearchResults/filters/SearchCheckboxFilter.vue";
import SearchRangeFilter from "@/pages/SearchResults/filters/SearchRangeFilter.vue";
import DateInput from "@/components/Global/DateInput.vue";
import SearchSelectionFilter from "@/pages/SearchResults/filters/SearchSelectionFilter.vue";
import CustomButton from "@/components/Button/Button.vue";


defineProps({
  openFilterModal: Boolean
});

const emit = defineEmits(["close"]);

const searchStore = useSearchStore();
const { t } = useI18n();

</script>

<style scoped>

.border-black {
  border-color: #000 !important;
}

.arrow__btn:hover img {
  filter: brightness(0) invert(1);
}

:deep(.el-select__wrapper.is-focused) {
  box-shadow: 0 0 0 1px #000 inset;
}

:deep(.el-select__wrapper.is-filterable .el-select__input:focus-visible) {
  outline: none;
}

:deep(.el-select__selected-item.el-select__input-wrapper) {
  box-shadow: unset;
}

:deep(.el-tag.el-tag--info) {
  background: black;
  --el-tag-text-color: #fff;
  font-size: 14px;
}

:deep(.tabs .el-skeleton) {
  width: 5vw;
}

:deep(.tabs .el-skeleton__item.el-skeleton__p ) {
  width: 4.5vw;
}

:deep(.boat-result-container .el-skeleton) {
  width: 8vw;
}

:deep(.boat-result-container .el-skeleton__item.el-skeleton__p ) {
  width: 6.5vw;
}

:deep(.boat-stats-container .el-skeleton) {
  width: 6vw;
}

:deep(.boat-stats-container .el-skeleton__item.el-skeleton__p ) {
  width: 4.5vw;
}

:deep(.el-loading-mask) {
  @apply -mt-4 -mr-2 z-20;
}


.border-black {
  border-color: #000000 !important;
}

.border-grey:hover {
  border-color: #000000 !important;
}


@keyframes dots {
  0%, 20% {
    color: rgba(0, 0, 0, 0);
    text-shadow: .25em 0 0 rgba(0, 0, 0, 0),
    .5em 0 0 rgba(0, 0, 0, 0);
  }
  40% {
    color: black;
    text-shadow: .25em 0 0 rgba(0, 0, 0, 0),
    .5em 0 0 rgba(0, 0, 0, 0);
  }
  60% {
    text-shadow: .25em 0 0 black,
    .5em 0 0 rgba(0, 0, 0, 0);
  }
  80%, 100% {
    text-shadow: .25em 0 0 black,
    .5em 0 0 black;
  }
}


.filter-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 350px;
  height: 100%;
  background-color: #fff;
  transform: translateX(100%);
  transition: transform 0.3s ease;
  z-index: 90;
  padding: 30px;
  overflow-y: auto;
}

.filter-drawer.open {
  transform: translateX(0);
}

@media (max-width: 768px) {
  .filter-drawer {
    width: 95%;
    border-radius: 20px 0 0 20px;
  }
}

.menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0rem;
  background-color: #fff;
}

.btnClose {
  border: 1px solid #B7B6BE;
  padding: 6px 16px;
  border-radius: 20px;
}


</style>